import React from 'react';
import { Controller, useForm, RegisterOptions } from 'react-hook-form';
import styled from 'styled-components';
import CountrySelect, { CountryOption, defaultCountry } from 'web/components/CountrySelect';
import { Button, FormError, FormGroup, Input, Label, LabelText } from 'web/components/elements';
import Spinner from 'web/components/Spinner';

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  country: CountryOption;
};

const NamesGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 12px;
`;

const maxNameLength = 40;

const nameValidationRules: RegisterOptions = {
  required: 'Required',
  minLength: {
    value: 2,
    message: 'Too short, please use at least 1 character',
  },
  maxLength: {
    value: maxNameLength,
    message: `Too long, please limit the name to ${maxNameLength} characters`,
  },
};

const emailValidationRules: RegisterOptions = {
  required: 'Required',
  pattern: {
    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
    message: 'Invalid email address',
  },
};

const countryValidationRules: RegisterOptions = {
  required: 'Required',
};

const UserCreateForm = ({
  onSubmit,
  submitting,
  error,
  withCountry = true,
  buttonSize = 'sm',
}: {
  onSubmit: (values: FormValues) => void;
  submitting: boolean;
  error: string | null;
  withCountry?: boolean;
  buttonSize?: 'xs' | 'sm' | 'md' | 'lg';
}) => {
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
  } = useForm<FormValues>();
  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <NamesGroup>
          <FormGroup>
            <Label>
              <Input
                {...register('firstName', nameValidationRules)}
                placeholder="Mary"
                hasError={!!errors.firstName}
                autoComplete="given-name"
              />
              <LabelText>First name</LabelText>
            </Label>
            {errors.firstName && <FormError>{errors.firstName.message}</FormError>}
          </FormGroup>
          <FormGroup>
            <Label>
              <Input
                {...register('lastName', nameValidationRules)}
                placeholder="Doe"
                hasError={!!errors.lastName}
                autoComplete="family-name"
              />
              <LabelText>Last name</LabelText>
            </Label>
            {errors.lastName && <FormError>{errors.lastName.message}</FormError>}
          </FormGroup>
        </NamesGroup>
        <FormGroup>
          <Label>
            <Input
              {...register('email', emailValidationRules)}
              placeholder="mary.doe@example.com"
              hasError={!!errors.email}
              autoComplete="email"
            />
            <LabelText>Email</LabelText>
          </Label>
          {errors.email && <FormError>{errors.email.message}</FormError>}
        </FormGroup>
        {withCountry && (
          <FormGroup>
            <Label>
              <Controller
                name="country"
                defaultValue={defaultCountry}
                rules={countryValidationRules}
                control={control}
                render={({ field }) => <CountrySelect {...field} withLabel />}
              />
              <LabelText>Country</LabelText>
            </Label>
          </FormGroup>
        )}
        <FormGroup>
          <Button type="submit" disabled={submitting} size={buttonSize}>
            {submitting && <Spinner />}
            <span>Create</span>
          </Button>
        </FormGroup>
        <FormGroup>{error && <FormError>{`${error}`}</FormError>}</FormGroup>
      </form>
    </div>
  );
};

export { FormValues };
export default UserCreateForm;
